import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Fade,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
  styled,
  type SxProps,
  type Theme,
} from "~/common/mui";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import type { StepStatus } from "../../contract/step";

export type { StepStatus } from "../../contract/step";

export interface StepData {
  title: string;
  subtitle?: string;
  status: StepStatus;
}

export interface ConfiguratorStepperProps {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  steps: StepData[];
  /**
   * Zero-based active step index. Values outside the available range are clamped for rendering.
   *
   * Status mapping:
   * - available -> medias Design Kit Inactive/Default
   * - completed -> Completed
   * - locked -> Not ready
   * - disabled -> Disabled
   * - active step renders as Active regardless of status
   */
  activeStep: number | null;
  onStepClick: (index: number) => void;
  /**
   * Attaches to the root Card for caller-owned layout and positioning.
   */
  className?: string;
  /**
   * Attaches to the root Card for caller-owned layout and positioning.
   */
  sx?: SxProps<Theme>;
}

type RenderedStepState = "active" | StepStatus;

const clampActiveStep = (activeStep: number, stepsLength: number) => {
  if (stepsLength === 0) {
    return 0;
  }

  return Math.min(Math.max(activeStep, 0), stepsLength - 1);
};

const getRenderedStepState = (status: StepStatus, index: number, activeStep: number | null): RenderedStepState =>
  index === activeStep ? "active" : status;

const getStatusLabel = (state: RenderedStepState) => {
  switch (state) {
    case "active":
      return "active";
    case "available":
      return "available";
    case "completed":
      return "completed";
    case "locked":
      return "not ready";
    case "disabled":
      return "disabled";
  }
};

const isStepClickable = (status: StepStatus, index: number, activeStep: number | null) =>
  index !== activeStep && (status === "available" || status === "completed");

const StepperRoot = styled(Card)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "16px",
  backgroundColor: theme.palette.surfaceContainer.lowest,
  transition: theme.transitions.create("width", {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  flexShrink: 0,
}));

const Header = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ProgressRing = styled(Box)(({ theme }) => ({
  position: "relative",
  width: theme.spacing(6),
  height: theme.spacing(6),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const Body = styled(Box)(({ theme }) => ({
  flex: "1 1 auto",
  padding: theme.spacing(2),
  minHeight: 0,
  overflowY: "auto",
  display: "flex",
}));

const Footer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  padding: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const StepLabelContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  minWidth: 0,
  width: expanded ? "auto" : 0,
  display: expanded ? "block" : "none",
  overflow: "hidden",
  marginLeft: expanded ? theme.spacing(1.5) : 0,
  whiteSpace: "normal",
}));

const StepIconCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "state",
})<{ state: RenderedStepState }>(({ state, theme }) => {
  const isActive = state === "active";
  const isCompleted = state === "completed";
  const isDisabled = state === "disabled";
  const isLocked = state === "locked";

  return {
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
    border: isActive ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
    backgroundColor: isActive
      ? theme.palette.primary.main
      : isCompleted
      ? theme.palette.success.container
      : isLocked
      ? theme.palette.surfaceContainer.high
      : theme.palette.surfaceContainer.main,
    color: isActive
      ? theme.palette.primary.contrastText
      : isCompleted
      ? theme.palette.success.onContainer
      : theme.palette.text.secondary,
    opacity: isDisabled ? theme.palette.action.disabledOpacity : 1,
  };
});

interface ConfiguratorStepIconProps {
  state: RenderedStepState;
  stepNumber: number;
}

const ConfiguratorStepIcon = ({ state, stepNumber }: ConfiguratorStepIconProps) => (
  <StepIconCircle state={state}>
    {state === "completed" ? (
      <CheckRoundedIcon fontSize={"small"} />
    ) : (
      <Typography variant="labelLarge" sx={{ color: "inherit" }}>
        {stepNumber}
      </Typography>
    )}
  </StepIconCircle>
);

interface FooterButtonProps {
  onClick: () => void;
  label: string;
}

const ExpandedFooterButton = ({ onClick, label }: FooterButtonProps) => (
  <Fade in timeout={180}>
    <Button
      color="secondary"
      variant="text"
      size="small"
      onClick={onClick}
      startIcon={<KeyboardArrowLeftIcon />}
      aria-label={label}
    >
      {label}
    </Button>
  </Fade>
);

const CollapsedFooterButton = ({ onClick, label }: FooterButtonProps) => (
  <Fade in timeout={180}>
    <Box component="span" sx={{ display: "flex", justifyContent: "center", width: "100%", minWidth: 0 }}>
      <TooltipWithArrow title={label} placement="right">
        <Button
          color="secondary"
          variant="text"
          size="small"
          onClick={onClick}
          aria-label={label}
          sx={(theme) => ({
            minWidth: theme.spacing(5),
            width: theme.spacing(5),
            height: theme.spacing(5),
            p: 0,
          })}
        >
          <KeyboardArrowRightIcon />
        </Button>
      </TooltipWithArrow>
    </Box>
  </Fade>
);

export const ConfiguratorStepper = ({
  expanded,
  onExpandedChange,
  steps,
  activeStep,
  onStepClick,
  className,
  sx,
}: ConfiguratorStepperProps) => {
  const { t } = useTranslation();

  const clampedActiveStep = activeStep === null ? null : clampActiveStep(activeStep, steps.length);
  const completedStepCount = steps.filter((step) => step.status === "completed").length;
  const currentProgress = clampedActiveStep === null ? completedStepCount : clampedActiveStep + 1;
  const progressText = `${steps.length === 0 ? 0 : currentProgress}/${steps.length}`;
  const progressValue = steps.length === 0 ? 0 : (currentProgress / steps.length) * 100;
  const progressLabel = t("feature.configurator.v2.stepper.progress", "Progress");
  const collapseLabel = t("feature.configurator.v2.stepper.collapse", "Collapse");
  const expandLabel = t("feature.configurator.v2.stepper.expand", "Expand");

  const renderedSteps = useMemo(
    () =>
      steps.map((step, index) => {
        const state = getRenderedStepState(step.status, index, clampedActiveStep);

        return {
          ...step,
          index,
          state,
          clickable: isStepClickable(step.status, index, clampedActiveStep),
          statusLabel: getStatusLabel(state),
        };
      }),
    [clampedActiveStep, steps],
  );

  return (
    <StepperRoot className={className} sx={sx}>
      <Header aria-label={`${progressLabel}: ${progressText}`}>
        <ProgressRing>
          <CircularProgress
            aria-hidden
            enableTrackSlot
            color="primary"
            size={48}
            thickness={4}
            variant="determinate"
            value={progressValue}
          />
          <Typography
            variant="bodySmall"
            sx={{
              position: "absolute",
              inset: 0,
              display: expanded ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.primary",
              fontWeight: 700,
            }}
          >
            {progressText}
          </Typography>
        </ProgressRing>
        <Fade in={expanded} timeout={180}>
          <Stack gap={1} sx={{ minWidth: 0, display: expanded ? undefined : "none" }} direction="row">
            <Typography variant="bodyLarge" sx={{ color: "text.label" }}>
              {progressLabel}
            </Typography>
            <Typography variant="bodyLarge" sx={{ color: "text.primary", fontWeight: 700 }}>
              {progressText}
            </Typography>
          </Stack>
        </Fade>
      </Header>

      <Body data-configurator-scroll-region="steps">
        <Stepper
          activeStep={clampedActiveStep ?? -1}
          nonLinear
          orientation="vertical"
          sx={(theme) => ({
            "& .MuiStepConnector-root": {
              // marginLeft: expanded ? undefined : 0,
              display: "flex",
              flexGrow: "1",
              minHeight: 40,
              height: 117,
              // justifyContent: expanded ? undefined : "center",
            },
          })}
        >
          {renderedSteps.map((step) => {
            const stepButton = (
              <StepButton
                icon={<ConfiguratorStepIcon state={step.state} stepNumber={step.index + 1} />}
                onClick={step.clickable ? () => onStepClick(step.index) : undefined}
                disabled={step.status === "disabled"}
                aria-disabled={step.status === "locked" ? true : undefined}
                aria-current={step.state === "active" ? "step" : undefined}
                aria-label={`Step ${step.index + 1}: ${step.title}, ${step.statusLabel}`}
                tabIndex={step.status === "locked" ? -1 : undefined}
                sx={(theme) => ({
                  justifyContent: "flex-start",
                  minHeight: "40px",
                  cursor: step.clickable ? "pointer" : "default",
                  color: "text.primary",
                  "& .MuiStepLabel-iconContainer": {
                    paddingRight: 0,
                  },
                  "& .MuiStepLabel-labelContainer": {
                    minWidth: 0,
                  },
                  "&.Mui-disabled": {
                    opacity: theme.palette.action.disabledOpacity,
                  },
                  "&:hover": {
                    backgroundColor: step.clickable ? theme.palette.action.hover : "transparent",
                  },
                })}
              >
                <Fade in={expanded} timeout={180}>
                  <StepLabelContent expanded={expanded} aria-hidden={!expanded}>
                    <Typography
                      variant="bodyMedium"
                      sx={{
                        color:
                          step.state === "active"
                            ? "primary.main"
                            : step.status === "disabled"
                            ? "text.disabled"
                            : step.status === "locked"
                            ? "text.label"
                            : "text.primary",
                        fontWeight: step.state === "active" ? 700 : 400,
                      }}
                    >
                      {step.title}
                    </Typography>
                    {step.subtitle && (
                      <Typography variant="bodySmall" sx={{ color: "text.secondary", mt: 0.25 }}>
                        {step.subtitle}
                      </Typography>
                    )}
                  </StepLabelContent>
                </Fade>
              </StepButton>
            );

            return (
              <Step
                key={`${step.title}-${step.index}`}
                active={step.state === "active"}
                completed={step.state === "completed"}
              >
                {expanded ? (
                  stepButton
                ) : (
                  <TooltipWithArrow title={step.title} placement="right">
                    <Box component="span" sx={{ display: "flex", width: "100%" }}>
                      {stepButton}
                    </Box>
                  </TooltipWithArrow>
                )}
              </Step>
            );
          })}
        </Stepper>
      </Body>

      <Footer>
        {expanded ? (
          <ExpandedFooterButton label={collapseLabel} onClick={() => onExpandedChange(false)} />
        ) : (
          <CollapsedFooterButton label={expandLabel} onClick={() => onExpandedChange(true)} />
        )}
      </Footer>
    </StepperRoot>
  );
};
